import { z } from "zod";
import { CasebookEndingSchema } from "./casebook.js";
import { ProseSchema } from "./prose.js";
import { RubricDimensionSchema } from "./primitives.js";
/**
 * THE PRACTICE RECORD — the progression layer's read surface.
 *
 * Gamified mechanics wearing professional clothes, and the clothes are load-
 * bearing: points and badges read as controlling and reliably undermine
 * intrinsic motivation, and the people using this are voluntarily practising
 * at 11pm — the most intrinsically motivated population there is. So the
 * vocabulary is a PRACTICE RECORD: techniques demonstrated, engagement
 * history, a development plan. No XP, no levels, no badges, no points — and
 * nothing in this schema can express a rank: there is no percentile field, no
 * position field, no cohort-ordering of any kind. A number a learner could
 * screenshot as a rank has nowhere to live.
 *
 * TALENT TREE, NOT ROGUELIKE BUILD (Law 6, ADR 0006): what carries over
 * between cases is the six-dimension skill vector and the demonstrated
 * repertoire; what resets is case state, and only case state. Unlocks REVEAL
 * options — they never grant power and never gate access.
 */
/** One demonstrated technique: a lead whose payoff was actually earned, in an engagement that was finished. */
export const DemonstratedTechniqueSchema = z.strictObject({
    /** The capability, in the learner's terms — the lead's authored `demonstrated` line. */
    text: ProseSchema,
    /** Which dimensions this behaviour exercises. Humanised client-side; never rendered as the enum. */
    trains: z.array(RubricDimensionSchema).min(1),
    /** Which client engagement it was shown on. */
    client: z.string().min(1),
});
/**
 * One revealed next step. AT MOST FIVE are ever returned, and usually three:
 * meta-progression's documented function is to LIMIT the option space and
 * widen it as mastery is shown — a full tree is noise wearing a map's clothes.
 */
export const NextStepSchema = z.strictObject({
    /** What to try, phrased as an option on the table — never an assignment, never a stat. */
    text: ProseSchema,
    dimension: RubricDimensionSchema,
});
/**
 * The shape paragraph's inputs. SHAPE, NOT RANK: leading and lagging are the
 * learner's own dimensions relative to THEIR OWN vector — strengths-based
 * framing, meaningful after a single scored case, and structurally incapable
 * of placing anyone above or below anyone else.
 */
export const SkillShapeSchema = z.strictObject({
    /** The learner's two strongest dimensions, by latest evidence. */
    leading: z.array(RubricDimensionSchema).length(2),
    /** The dimension currently furthest behind. */
    lagging: RubricDimensionSchema,
    /**
     * How many OTHER learners currently share this shape. Rendered only as the
     * phrase "among learners with this shape" once it clears a floor — never as
     * a count the learner sees, and never as a position among them.
     */
    cohort_n: z.number().int().min(0),
});
/** One dimension's feedback over time. `sufficient_n` gates the TREND, not the record: two points is not a trend. */
export const DimensionTrendSchema = z.strictObject({
    dimension: RubricDimensionSchema,
    /** Median score per completed engagement, oldest first. Empty until something is scored. */
    points: z.array(z.number().min(1).max(5)),
    sufficient_n: z.boolean(),
});
export const ReflectionStateSchema = z.strictObject({
    /** Today's reflection question, in the mentor's register. */
    question: ProseSchema,
    /** True once today's reflection is written. */
    answered_today: z.boolean(),
    /** Consecutive days of reflection, counting back from today or yesterday. The streak is the REFLECTION's, never the case's. */
    running_days: z.number().int().min(0),
});
export const PracticeRecordSchema = z.strictObject({
    /**
     * Who this record is about, from the server's own confirmed profile row —
     * so a returning learner on a fresh browser still sees themselves. Null only
     * when onboarding has never been finished, and the client gates the coach
     * and the reflection on exactly this rather than on local storage.
     */
    profile: z
        .strictObject({
        name: z.string().min(1),
        experience_level: z.string().min(1),
        approaches: z.array(z.string().min(1)),
        /** True until a finished engagement has shown how the learner works (onboarding pass, 2026-09-13). */
        approaches_provisional: z.boolean(),
        /** True once the learner has made, or declined, the one rehearsal move before their first case. */
        rehearsal_done: z.boolean(),
    })
        .nullable(),
    /**
     * Finished engagements, oldest first: client name and when it closed, and
     * (2026-09-24) the session and how it ended, so each row opens its casebook.
     */
    engagement_history: z.array(z.strictObject({ session_id: z.string().min(1), client: z.string().min(1), completed_at: z.iso.datetime(), ending: CasebookEndingSchema })),
    techniques: z.array(DemonstratedTechniqueSchema),
    trends: z.array(DimensionTrendSchema),
    /** Null until at least one engagement has scored evidence. */
    shape: SkillShapeSchema.nullable(),
    /** 3-5 revealed options, or empty when there is no evidence to reveal from. */
    next_steps: z.array(NextStepSchema).max(5),
    reflection: ReflectionStateSchema,
});
//# sourceMappingURL=practice-record.js.map